import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, TrendingDown, CheckCheck, BellRing } from "lucide-react";
import { useApp } from "@/context";
import http from "@/api";
import { pushSupported, pushPermission, subscribePush } from "@/push";

// Lightweight header bell: polls /api/notifications every 45s for logged-in
// users. Click to open dropdown; click a row to mark as read + go to product.
export default function NotificationsBell() {
  const { user, lang, formatPrice } = useApp();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [perm, setPerm] = useState(null);
  const [subBusy, setSubBusy] = useState(false);
  const ref = useRef(null);

  // Check push permission state once dropdown opens
  useEffect(() => {
    if (!open || !pushSupported()) return;
    pushPermission().then(setPerm);
  }, [open]);

  const enablePush = async () => {
    setSubBusy(true);
    try {
      const res = await subscribePush();
      if (res.ok) {
        setPerm("granted");
      } else if (res.reason === "denied") {
        setPerm("denied");
      }
    } finally { setSubBusy(false); }
  };

  useEffect(() => {
    if (!user) return;
    let stop = false;
    const load = async () => {
      try {
        const { data } = await http.get("/notifications?limit=15");
        if (stop) return;
        setItems(data?.items || []);
        setUnread(data?.unread || 0);
      } catch { /* non-fatal */ }
    };
    load();
    const id = setInterval(load, 45000);
    return () => { stop = true; clearInterval(id); };
  }, [user]);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!user) return null;

  const markRead = async (n) => {
    try { await http.post(`/notifications/${n.id}/read`); } catch {}
    setItems((arr) => arr.map((x) => x.id === n.id ? { ...x, read: true } : x));
    setUnread((u) => Math.max(0, u - (n.read ? 0 : 1)));
  };

  const markAllRead = async () => {
    try { await http.post("/notifications/read-all"); } catch {}
    setItems((arr) => arr.map((x) => ({ ...x, read: true })));
    setUnread(0);
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)}
              className="relative p-2 rounded-full hover:bg-black/5"
              data-testid="notif-bell-btn"
              aria-label={lang === "ar" ? "الإشعارات" : "Notifications"}>
        <Bell size={22} style={unread > 0 ? { color: "#dc2626" } : undefined} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 text-white text-[10px] rounded-full px-1.5 py-0.5 min-w-[18px] text-center"
                style={{ background: "#dc2626" }}
                data-testid="notif-bell-count">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute mt-2 w-[320px] rounded-xl shadow-xl border overflow-hidden z-50"
             style={{ background: "#fff", borderColor: "var(--border)",
                      [lang === "ar" ? "left" : "right"]: 0 }}
             data-testid="notif-dropdown">
          <div className="px-3 py-2 border-b flex items-center justify-between"
               style={{ borderColor: "var(--border)" }}>
            <div className="font-semibold">{lang === "ar" ? "الإشعارات" : "Notifications"}</div>
            {unread > 0 && (
              <button onClick={markAllRead}
                      className="text-xs font-semibold hover:underline"
                      style={{ color: "var(--primary)" }}
                      data-testid="notif-mark-all-read">
                {lang === "ar" ? `تعليم الكل كمقروء (${unread})` : `Mark all read (${unread})`}
              </button>
            )}
          </div>

          {/* Enable Browser Push CTA — only when supported + not yet granted */}
          {pushSupported() && perm === "default" && (
            <div className="px-3 py-2.5 border-b space-y-2"
                 style={{ borderColor: "var(--border)", background: "rgba(220,38,38,0.05)" }}
                 data-testid="push-enable-banner">
              <div className="flex items-start gap-2">
                <BellRing size={16} style={{ color: "#dc2626", marginTop: 2 }} />
                <div className="flex-1">
                  <div className="text-xs font-semibold">
                    {lang === "ar" ? "فعّل إشعارات المتصفّح" : "Enable browser alerts"}
                  </div>
                  <div className="text-[11px] opacity-70">
                    {lang === "ar"
                      ? "تنبيهات انخفاض الأسعار حتى لو التطبيق مغلق."
                      : "Get price-drop alerts even when the app is closed."}
                  </div>
                </div>
              </div>
              <button onClick={enablePush} disabled={subBusy}
                      className="w-full text-xs font-semibold py-1.5 rounded-lg text-white disabled:opacity-60"
                      style={{ background: "#dc2626" }}
                      data-testid="push-enable-btn">
                {subBusy ? "..." : (lang === "ar" ? "تفعيل الآن" : "Enable now")}
              </button>
            </div>
          )}

          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm opacity-60" data-testid="notif-empty">
              {lang === "ar" ? "لا توجد إشعارات بعد" : "No notifications yet"}
            </div>
          ) : (
            <div className="max-h-[380px] overflow-y-auto">
              {items.map((n) => (
                <Link
                  key={n.id}
                  to={n.product_id ? `/p/${n.product_id}` : "/"}
                  onClick={() => { markRead(n); setOpen(false); }}
                  className="flex items-start gap-3 px-3 py-2.5 border-b hover:bg-black/5 transition-colors"
                  style={{ borderColor: "var(--border)",
                           background: n.read ? "transparent" : "rgba(220,38,38,0.04)" }}
                  data-testid={`notif-row-${n.id}`}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                       style={{ background: n.type === "price_drop" ? "#fee2e2" : "#f3f4f6" }}>
                    {n.type === "price_drop"
                      ? <TrendingDown size={16} style={{ color: "#dc2626" }} />
                      : <Bell size={16} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium line-clamp-2">
                      {lang === "ar" ? n.title_ar : n.title_en}
                    </div>
                    {n.type === "price_drop" && n.new_price != null && (
                      <div className="text-xs mt-0.5 flex items-center gap-1.5">
                        <span className="line-through opacity-50">{formatPrice(n.old_price)}</span>
                        <span className="font-bold" style={{ color: "#dc2626" }}>
                          {formatPrice(n.new_price)}
                        </span>
                      </div>
                    )}
                    <div className="text-[10px] opacity-50 mt-0.5">
                      {new Date(n.created_at).toLocaleString(lang === "ar" ? "ar-QA" : "en-US",
                        { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                  {!n.read && <div className="w-2 h-2 rounded-full mt-2" style={{ background: "#dc2626" }} />}
                </Link>
              ))}
            </div>
          )}

          {items.length > 0 && (
            <Link to="/favorites" onClick={() => setOpen(false)}
                  className="block text-center py-2 text-xs font-semibold border-t hover:bg-black/5"
                  style={{ borderColor: "var(--border)", color: "var(--primary)" }}
                  data-testid="notif-see-all">
              <CheckCheck size={14} className="inline me-1" />
              {lang === "ar" ? "عرض كل المفضّلة والعروض" : "See all favorites & deals"}
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
